import type { ForceUnit, ForceUnitInfo } from '@sb/remote-control/types';
import { roundToDecimalPlaces } from '@sb/utilities';

function using({
  abbreviation = 'N',
  decimalPlaces = 1,
  multiplier = 1,
}): Pick<
  ForceUnitInfo,
  'decimalPlaces' | 'multiplier' | 'fromNewtons' | 'toNewtons' | 'toString'
> {
  return {
    decimalPlaces,
    multiplier,
    fromNewtons: (value, roundingKind?: 'round' | 'floor' | 'ceil') =>
      typeof value === 'number'
        ? roundToDecimalPlaces(value * multiplier, decimalPlaces, roundingKind)
        : value,
    toNewtons: (value) =>
      typeof value === 'number' ? value / multiplier : value,
    toString: (
      value,
      roundingKind?: 'round' | 'floor' | 'ceil',
      excludeAbbreviation = false,
    ) => {
      const v =
        typeof value === 'number'
          ? `${roundToDecimalPlaces(value * multiplier, decimalPlaces, roundingKind)}`
          : `${value}`;

      if (excludeAbbreviation) {
        return v;
      }

      return `${v} ${abbreviation}`;
    },
  };
}

export const FORCE_UNIT_INFOS: Array<ForceUnitInfo> = [
  {
    unit: 'newton',
    name: 'Newton',
    namePlural: 'Newtons',
    abbreviation: 'N',
    ...using({ abbreviation: 'N', decimalPlaces: 1, multiplier: 1 }),
  },
  {
    unit: 'pound-force',
    name: 'Pound-Force',
    namePlural: 'Pounds-Force',
    abbreviation: 'lbf',
    ...using({
      abbreviation: 'lbf',
      decimalPlaces: 2,
      multiplier: 0.224809, // 1 pound-force = 4.44822 Newtons
    }),
  },
];

export function getForceUnitInfo(unit?: ForceUnit): ForceUnitInfo {
  return (
    FORCE_UNIT_INFOS.find((info) => info.unit === unit) ?? FORCE_UNIT_INFOS[0]
  );
}
